import React, { useState, useEffect, useCallback } from 'react';
import { ControllerOptions } from '../../hooks/useControllerOptions';
import { fetchData, postData, updateData } from '../../api';
import { useDatabaseSettings, DatabaseSettings, StorageDevice, StorageStatus } from '../../hooks/useHistoricalData';
import { useNetworkSettings, NetworkStatus, WiFiNetwork, EthernetSettings as EthernetSettingsType, APSettings } from '../../hooks/useNetworkSettings';

// Move reusable input components OUTSIDE the main component to prevent re-mounting
const TextInput = ({ label, value, onChange, placeholder, disabled }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) => (
  <div>
    <label className="block text-sm text-slate-400 mb-1">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      disabled={disabled}
      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
    />
  </div>
);

const NumberInput = ({ label, value, onChange, unit, min, max, step, disabled }: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  unit?: string;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
}) => (
  <div>
    <label className="block text-sm text-slate-400 mb-1">{label}</label>
    <div className="relative">
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
      />
      {unit && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">{unit}</span>
      )}
    </div>
  </div>
);

const Toggle = ({ label, description, checked, onChange }: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <div className="flex items-center justify-between py-2">
    <div>
      <div className="text-sm text-slate-200">{label}</div>
      {description && <div className="text-xs text-slate-500">{description}</div>}
    </div>
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-slate-600'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${checked ? 'translate-x-5' : ''}`}
      />
    </button>
  </div>
);

const SectionHeader = ({ title, description }: { title: string; description?: string }) => (
  <div className="mb-4">
    <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wide">{title}</h3>
    {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
  </div>
);

// ============================================================================
// CONFIGURATION
// Uses browser hostname as default IP (assumes logger and devices on same network)
// ============================================================================

// Get the current hostname/IP from the browser URL
const getDefaultIp = () => {
  const hostname = window.location.hostname;
  // If localhost or 127.0.0.1, we're in dev mode
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return '127.0.0.1';
  }
  return hostname;
};

const DEV_MODE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Default values - uses current hostname as default IP for device scanning
const CONFIG = {
  // Device Communications
  scan: {
    startIp: getDefaultIp(),  // Uses the IP you're accessing the frontend from
    startPort: DEV_MODE ? 8502 : 502,
    numberOfPorts: DEV_MODE ? 10 : 5,
    numberOfIps: DEV_MODE ? 1 : 10,
    scanTimeout: DEV_MODE ? 500 : 1000,
  },
  // Network
  network: {
    defaultIp: getDefaultIp(),
    defaultGateway: DEV_MODE ? '127.0.0.1' : '192.168.1.1',
  },
  // External Services
  services: {
    apiPort: DEV_MODE ? 3000 : 80,
    mqttPort: 1883,
    tcpPort: DEV_MODE ? 5000 : 502,
  },
};

// Extended settings types (frontend-only for now, backend will be added later)
interface ExternalServices {
  apiEnabled: boolean;
  apiPort: number;
  apiAuthEnabled: boolean;
  apiKey: string;
  mqttEnabled: boolean;
  mqttBroker: string;
  mqttPort: number;
  mqttUsername: string;
  mqttPassword: string;
  mqttTopic: string;
  tcpEnabled: boolean;
  tcpHost: string;
  tcpPort: number;
}

interface ScanSettings {
  startIp: string;
  startPort: number;
  numberOfPorts: number;
  numberOfIps: number;
  scanTimeout: number;
  autoRescan: boolean;
  rescanInterval: number;
}

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  options: ControllerOptions | null;
  onSave: (options: ControllerOptions) => Promise<boolean>;
  saving: boolean;
}

interface DeviceConfig {
  portId: string;
  deviceIndex: string;
  type: string;
  model: string;
  ratedPower: number;
  sleeping: boolean;
}

type TabId = 'network' | 'services' | 'export' | 'advanced' | 'communications' | 'database' | 'system';

const tabs: { id: TabId; label: string; icon: JSX.Element }[] = [
  {
    id: 'network',
    label: 'Network',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
  },
  {
    id: 'services',
    label: 'External Services',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
      </svg>
    ),
  },
  {
    id: 'export',
    label: 'Export Management',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    id: 'advanced',
    label: 'Advanced Control',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    id: 'communications',
    label: 'Device Comms',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
      </svg>
    ),
  },
  {
    id: 'database',
    label: 'Database',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
      </svg>
    ),
  },
  {
    id: 'system',
    label: 'System',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
      </svg>
    ),
  },
];

interface ScanStatus {
  state: string;
  isScanning: boolean;
  portCount: number;
  deviceCount: number;
  inverterCount: number;
  hybridCount: number;
  meterCount: number;
}

export function SettingsModal({ isOpen, onClose, options, onSave, saving }: SettingsModalProps) {
  const [activeTab, setActiveTab] = useState<TabId>('export');
  const [formData, setFormData] = useState<ControllerOptions | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanStatus, setScanStatus] = useState<ScanStatus | null>(null);
  const [scanError, setScanError] = useState<string | null>(null);

  // Network settings hook - manages WiFi, ethernet, and AP
  const {
    status: networkStatus,
    networks: wifiNetworks,
    ethernetSettings: fetchedEthernetSettings,
    apSettings: fetchedApSettings,
    loading: networkLoading,
    scanning: wifiScanning,
    connecting: wifiConnecting,
    disconnecting: wifiDisconnecting,
    startingAP,
    stoppingAP,
    error: networkError,
    scanWiFi,
    connectWiFi,
    disconnectWiFi,
    setEthernet,
    startAP,
    stopAP,
    generateAPName,
    setHostname,
    factoryReset,
    refreshStatus: refreshNetworkStatus,
    clearError: clearNetworkError,
  } = useNetworkSettings();

  // Network sub-tab state
  const [networkSubTab, setNetworkSubTab] = useState<'wifi' | 'ethernet' | 'ap'>('wifi');
  const [selectedWifiNetwork, setSelectedWifiNetwork] = useState<string | null>(null);
  const [wifiPassword, setWifiPassword] = useState('');
  const [showWifiPassword, setShowWifiPassword] = useState(false);
  const [localEthernetSettings, setLocalEthernetSettings] = useState<EthernetSettingsType>({
    dhcp: true,
    ip: '',
    subnet: '255.255.255.0',
    gateway: '',
    dns: ['8.8.8.8'],
  });
  const [localApSSID, setLocalApSSID] = useState('');
  const [localApPassword, setLocalApPassword] = useState('');
  const [showApPassword, setShowApPassword] = useState(false);
  const [localHostname, setLocalHostname] = useState('');
  const [factoryResetConfirm, setFactoryResetConfirm] = useState(false);

  // Sync local ethernet settings when fetched
  useEffect(() => {
    if (fetchedEthernetSettings) {
      setLocalEthernetSettings({
        dhcp: fetchedEthernetSettings.dhcp,
        ip: fetchedEthernetSettings.ip || '',
        subnet: fetchedEthernetSettings.subnet || '255.255.255.0',
        gateway: fetchedEthernetSettings.gateway || '',
        dns: fetchedEthernetSettings.dns || ['8.8.8.8'],
      });
    }
  }, [fetchedEthernetSettings]);

  // Sync local AP settings when fetched
  useEffect(() => {
    if (fetchedApSettings) {
      setLocalApSSID(fetchedApSettings.ssid || '');
      setLocalApPassword(fetchedApSettings.password || '');
    }
  }, [fetchedApSettings]);

  // Sync hostname from network status
  useEffect(() => {
    if (networkStatus?.hostname && !localHostname) {
      setLocalHostname(networkStatus.hostname);
    }
  }, [networkStatus, localHostname]);

  // External services state (frontend-only for now)
  const [externalServices, setExternalServices] = useState<ExternalServices>({
    apiEnabled: true,
    apiPort: CONFIG.services.apiPort,
    apiAuthEnabled: false,
    apiKey: '',
    mqttEnabled: false,
    mqttBroker: '',
    mqttPort: CONFIG.services.mqttPort,
    mqttUsername: '',
    mqttPassword: '',
    mqttTopic: 'solar/controller',
    tcpEnabled: false,
    tcpHost: '',
    tcpPort: CONFIG.services.tcpPort,
  });

  // Scan settings state
  const [scanSettings, setScanSettings] = useState<ScanSettings>({
    startIp: CONFIG.scan.startIp,
    startPort: CONFIG.scan.startPort,
    numberOfPorts: CONFIG.scan.numberOfPorts,
    numberOfIps: CONFIG.scan.numberOfIps,
    scanTimeout: CONFIG.scan.scanTimeout,
    autoRescan: false,
    rescanInterval: 60,
  });

  // Device configuration state
  const [deviceConfigs, setDeviceConfigs] = useState<DeviceConfig[]>([]);
  const [deviceConfigsLoaded, setDeviceConfigsLoaded] = useState(false);
  const [expandedPort, setExpandedPort] = useState<string | null>(null);
  const [editingDevice, setEditingDevice] = useState<string | null>(null);

  // Database settings state
  const {
    settings: dbSettings,
    devices: storageDevices,
    storageStatus,
    saving: dbSaving,
    saveSettings: saveDbSettings,
    clearLogs,
    refetchDevices,
    refetchStatus,
  } = useDatabaseSettings();
  const [localDbSettings, setLocalDbSettings] = useState<DatabaseSettings | null>(null);
  const [isClearing, setIsClearing] = useState(false);

  // Track initialization separately for controller options and scan options
  const [hasInitialized, setHasInitialized] = useState(false);
  const [scanSettingsLoaded, setScanSettingsLoaded] = useState(false);

  // Load controller options from props
  useEffect(() => {
    if (options && !hasInitialized) {
      setFormData({ ...options });
      setHasInitialized(true);
    }
  }, [options, hasInitialized]);

  // Sync local database settings when loaded
  useEffect(() => {
    if (dbSettings && !localDbSettings) {
      setLocalDbSettings({ ...dbSettings });
    }
  }, [dbSettings, localDbSettings]);

  // Load scan options from backend when modal opens (always load fresh)
  useEffect(() => {
    if (isOpen && !scanSettingsLoaded) {
      fetchData('/scanOptions')
        .then((backendScanOptions: any) => {
          if (backendScanOptions) {
            // Convert backend format to frontend format
            setScanSettings({
              startIp: backendScanOptions.startIP || CONFIG.scan.startIp,
              startPort: backendScanOptions.startPort || CONFIG.scan.startPort,
              numberOfPorts: backendScanOptions.endPort && backendScanOptions.startPort
                ? backendScanOptions.endPort - backendScanOptions.startPort + 1
                : CONFIG.scan.numberOfPorts,
              numberOfIps: (backendScanOptions.endIP ?? 0) + 1,
              scanTimeout: CONFIG.scan.scanTimeout,
              autoRescan: false,
              rescanInterval: 60,
            });
            setScanSettingsLoaded(true);
          }
        })
        .catch(err => {
          console.log('Could not load scan options from backend:', err.message);
          setScanSettingsLoaded(true); // Mark as loaded even on error to prevent retry loop
        });
    }
  }, [isOpen, scanSettingsLoaded]);

  // Reset initialization state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setHasInitialized(false);
      setScanSettingsLoaded(false);
      setDeviceConfigsLoaded(false);
      setDeviceConfigs([]);
      setExpandedPort(null);
      setEditingDevice(null);
      setLocalDbSettings(null);
    }
  }, [isOpen]);

  // Load device data when communications tab is active
  useEffect(() => {
    if (isOpen && activeTab === 'communications' && !deviceConfigsLoaded) {
      fetchData('/deviceData')
        .then((data: any) => {
          if (data) {
            const configs: DeviceConfig[] = [];
            for (const portId in data) {
              for (const deviceIndex in data[portId]) {
                const device = data[portId][deviceIndex];
                configs.push({
                  portId,
                  deviceIndex,
                  type: device.type || 'unknown',
                  model: device.model || '',
                  ratedPower: device.ratedPower || 0,
                  sleeping: device.sleeping || false,
                });
              }
            }
            setDeviceConfigs(configs);
            setDeviceConfigsLoaded(true);
          }
        })
        .catch(err => {
          console.log('Could not load device data:', err.message);
          setDeviceConfigsLoaded(true);
        });
    }
  }, [isOpen, activeTab, deviceConfigsLoaded]);

  if (!isOpen || !formData) return null;

  const handleChange = (key: keyof ControllerOptions, value: number | boolean) => {
    setFormData(prev => prev ? { ...prev, [key]: value } : null);
  };

  // Calculate appropriate soft limit based on hard limit
  // If <= 10kW: soft = hard - 100
  // If > 10kW: soft = hard - 1% of hard
  const calculateSoftLimit = (hardLimit: number): number => {
    const absHard = Math.abs(hardLimit);
    if (absHard <= 10000) {
      return hardLimit - 100;
    } else {
      return Math.round(hardLimit - (absHard * 0.01));
    }
  };

  // Handler for power hard limit changes - auto-updates soft limit
  const handlePowerHardLimitChange = (value: number) => {
    const newSoftLimit = calculateSoftLimit(value);
    setFormData(prev => prev ? {
      ...prev,
      exportPowerHardLimit: value,
      exportPowerSoftLimit: newSoftLimit,
    } : null);
  };

  // Handler for current hard limit changes - auto-updates soft limit
  const handleCurrentHardLimitChange = (value: number) => {
    // For current: if <= 100A, soft = hard - 5A; else soft = hard - 5%
    const absHard = Math.abs(value);
    const newSoftLimit = absHard <= 100 ? value - 5 : Math.round(value - (absHard * 0.05));
    setFormData(prev => prev ? {
      ...prev,
      exportCurrentHardLimit: value,
      exportCurrentSoftLimit: newSoftLimit,
    } : null);
  };

  // Handler for voltage hard limit changes - auto-updates soft limit
  const handleVoltageHardLimitChange = (value: number) => {
    // For voltage: soft = hard - 2V
    setFormData(prev => prev ? {
      ...prev,
      voltageHardLimit: value,
      voltageSoftLimit: value - 2,
    } : null);
  };

  // Validate soft limit is below hard limit
  const handleSoftLimitChange = (
    softKey: keyof ControllerOptions,
    hardKey: keyof ControllerOptions,
    value: number
  ) => {
    const hardValue = formData[hardKey] as number;
    // Ensure soft limit is below hard limit
    const validatedValue = Math.min(value, hardValue - 1);
    setFormData(prev => prev ? { ...prev, [softKey]: validatedValue } : null);
  };

  const handleSave = async () => {
    if (formData) {
      // Save scan settings to backend
      try {
        await updateData('/setScanOptions', {
          startIP: scanSettings.startIp,
          endIP: scanSettings.numberOfIps - 1,
          startPort: scanSettings.startPort,
          endPort: scanSettings.startPort + scanSettings.numberOfPorts - 1,
        });
      } catch (err) {
        console.error('Failed to save scan settings:', err);
      }

      // Save database settings if modified
      if (localDbSettings) {
        try {
          await saveDbSettings(localDbSettings);
        } catch (err) {
          console.error('Failed to save database settings:', err);
        }
      }

      // Save controller options
      const success = await onSave(formData);
      if (success) {
        onClose();
      }
    }
  };

  const handleRescan = async () => {
    setIsScanning(true);
    setScanProgress(0);
    setScanError(null);
    setScanStatus(null);

    try {
      // First, send the scan settings to the backend
      // Backend expects: startIP, endIP (offset from last octet), startPort, endPort
      await updateData('/setScanOptions', {
        startIP: scanSettings.startIp,
        endIP: scanSettings.numberOfIps - 1,  // endIP is offset from startIP's last octet
        startPort: scanSettings.startPort,
        endPort: scanSettings.startPort + scanSettings.numberOfPorts - 1,
      });
      setScanProgress(5);

      // Then trigger the rescan on the backend
      await postData('/rescan');
      setScanProgress(10);

      // Poll for scan status until complete
      const pollStatus = async () => {
        let attempts = 0;
        const maxAttempts = 30; // 30 seconds max

        const poll = setInterval(async () => {
          attempts++;
          try {
            const status = await fetchData('/status') as ScanStatus;
            setScanStatus(status);

            // Update progress based on scan state
            if (status.isScanning) {
              setScanProgress(Math.min(10 + attempts * 3, 90));
            } else {
              // Scan complete
              setScanProgress(100);
              clearInterval(poll);
              setTimeout(() => {
                setIsScanning(false);
              }, 500);
            }

            // Timeout after max attempts
            if (attempts >= maxAttempts) {
              clearInterval(poll);
              setIsScanning(false);
              setScanError('Scan timeout - check controller logs');
            }
          } catch (err) {
            console.error('Error polling status:', err);
          }
        }, 1000);
      };

      pollStatus();
    } catch (err) {
      console.error('Error triggering rescan:', err);
      setScanError('Failed to trigger rescan - is the controller running?');
      setIsScanning(false);
    }
  };

  // Signal strength bar component
  const SignalBars = ({ signal }: { signal: number }) => {
    const bars = [20, 40, 60, 80];
    return (
      <div className="flex items-end gap-0.5 h-4">
        {bars.map((threshold, i) => (
          <div
            key={i}
            className={`w-1 rounded-sm ${
              signal >= threshold ? 'bg-green-500' : 'bg-slate-600'
            }`}
            style={{ height: `${(i + 1) * 25}%` }}
          />
        ))}
      </div>
    );
  };

  // Handlers for network operations
  const handleWifiConnect = async () => {
    if (!selectedWifiNetwork || !wifiPassword) return;
    const success = await connectWiFi(selectedWifiNetwork, wifiPassword);
    if (success) {
      setWifiPassword('');
      setSelectedWifiNetwork(null);
    }
  };

  const handleEthernetSave = async () => {
    await setEthernet(localEthernetSettings);
  };

  const handleStartAP = async () => {
    await startAP(localApSSID || undefined, localApPassword || undefined);
  };

  const handleHostnameSave = async () => {
    if (localHostname) {
      await setHostname(localHostname);
    }
  };

  const handleFactoryReset = async () => {
    if (!factoryResetConfirm) {
      setFactoryResetConfirm(true);
      return;
    }
    await factoryReset();
    setFactoryResetConfirm(false);
  };

  const handleGenerateAPName = async () => {
    const name = await generateAPName();
    setLocalApSSID(name);
  };

  const renderNetworkTab = () => (
    <div className="space-y-6">
      {/* Network Status Banner */}
      {networkStatus && (
        <div className={`rounded-lg p-4 border ${
          networkStatus.mode === 'ap'
            ? 'bg-purple-900/30 border-purple-700'
            : networkStatus.wifi.connected || networkStatus.ethernet.connected
            ? 'bg-green-900/30 border-green-700'
            : 'bg-amber-900/30 border-amber-700'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${
                networkStatus.mode === 'ap' ? 'bg-purple-400' :
                networkStatus.internet ? 'bg-green-400' :
                networkStatus.wifi.connected || networkStatus.ethernet.connected ? 'bg-amber-400' :
                'bg-red-400'
              }`} />
              <div>
                <div className="text-sm font-medium text-white">
                  {networkStatus.mode === 'ap' ? 'Access Point Mode' :
                   networkStatus.wifi.connected && networkStatus.ethernet.connected ? `WiFi: ${networkStatus.wifi.ssid} + Ethernet` :
                   networkStatus.wifi.connected ? `Connected to ${networkStatus.wifi.ssid}` :
                   networkStatus.ethernet.connected ? 'Ethernet Connected' :
                   'Disconnected'}
                </div>
                <div className="text-xs text-slate-400">
                  {networkStatus.mode === 'ap'
                    ? `${networkStatus.ap.clients} client(s) connected`
                    : networkStatus.wifi.connected && networkStatus.ethernet.connected
                    ? `WiFi: ${networkStatus.wifi.ip} • Eth: ${networkStatus.ethernet.ip}`
                    : networkStatus.wifi.ip || networkStatus.ethernet.ip || 'No IP address'}
                  {networkStatus.internet && ' • Internet available'}
                </div>
              </div>
            </div>
            <div className="text-xs text-slate-400">
              Hostname: <span className="text-white font-mono">{networkStatus.hostname}</span>
            </div>
          </div>
        </div>
      )}

      {/* Network Error */}
      {networkError && (
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-300 text-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {networkError}
          </div>
          <button onClick={clearNetworkError} className="text-red-400 hover:text-red-300">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Sub-tabs: WiFi | Ethernet | Access Point */}
      <div className="flex gap-1 border-b border-slate-700 pb-1">
        {(['wifi', 'ethernet', 'ap'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setNetworkSubTab(tab)}
            className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              networkSubTab === tab
                ? 'bg-slate-700 text-white'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
            }`}
          >
            {tab === 'wifi' ? 'WiFi' : tab === 'ethernet' ? 'Ethernet' : 'Access Point'}
          </button>
        ))}
      </div>

      {/* WiFi Panel */}
      {networkSubTab === 'wifi' && (
        <div className="space-y-4">
          {/* Current WiFi Status */}
          {networkStatus?.wifi.connected && (
            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-slate-400">Connected to</div>
                  <div className="text-lg font-medium text-white flex items-center gap-2">
                    {networkStatus.wifi.ssid}
                    <SignalBars signal={networkStatus.wifi.signal} />
                    <span className="text-sm text-slate-400">({networkStatus.wifi.signal}%)</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">IP: {networkStatus.wifi.ip}</div>
                </div>
                <button
                  onClick={disconnectWiFi}
                  disabled={wifiDisconnecting}
                  className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:cursor-wait rounded text-sm text-slate-200 flex items-center gap-2"
                >
                  {wifiDisconnecting ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Disconnecting...
                    </>
                  ) : (
                    'Disconnect'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Available Networks */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <SectionHeader title="Available Networks" />
              <button
                onClick={scanWiFi}
                disabled={wifiScanning}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 rounded text-sm text-white flex items-center gap-1"
              >
                {wifiScanning ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Scanning...
                  </>
                ) : 'Scan'}
              </button>
            </div>

            {wifiNetworks.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                {wifiScanning ? 'Scanning for networks...' : 'No networks found. Click Scan to search.'}
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {wifiNetworks.map((network) => (
                  <div
                    key={network.ssid}
                    onClick={() => !network.connected && setSelectedWifiNetwork(network.ssid)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      network.connected
                        ? 'bg-green-900/20 border-green-700'
                        : selectedWifiNetwork === network.ssid
                        ? 'bg-blue-900/20 border-blue-500'
                        : 'bg-slate-900 border-slate-700 hover:border-slate-500'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {network.connected && (
                          <div className="w-2 h-2 rounded-full bg-green-400" />
                        )}
                        <span className="text-white">{network.ssid}</span>
                        {network.security !== 'open' && (
                          <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <SignalBars signal={network.signal} />
                        <span className="text-xs text-slate-500 uppercase">{network.security}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Password Input */}
            {selectedWifiNetwork && (
              <div className="mt-4 pt-4 border-t border-slate-700">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-slate-400">Connecting to:</span>
                  <span className="text-white font-medium">{selectedWifiNetwork}</span>
                  <button
                    onClick={() => { setSelectedWifiNetwork(null); setWifiPassword(''); }}
                    className="text-slate-500 hover:text-slate-300 ml-auto"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input
                      type={showWifiPassword ? 'text' : 'password'}
                      value={wifiPassword}
                      onChange={(e) => setWifiPassword(e.target.value)}
                      placeholder="Enter WiFi password"
                      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                      onKeyDown={(e) => e.key === 'Enter' && handleWifiConnect()}
                    />
                    <button
                      onClick={() => setShowWifiPassword(!showWifiPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      {showWifiPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  <button
                    onClick={handleWifiConnect}
                    disabled={!wifiPassword || wifiConnecting}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 disabled:text-slate-500 rounded-lg text-white font-medium"
                  >
                    {wifiConnecting ? 'Connecting...' : 'Connect'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ethernet Panel */}
      {networkSubTab === 'ethernet' && (
        <div className="space-y-4">
          {/* Current Ethernet Status */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm text-slate-400">Status</div>
                <div className={`text-lg font-medium ${networkStatus?.ethernet.connected ? 'text-green-400' : 'text-slate-500'}`}>
                  {networkStatus?.ethernet.connected ? 'Connected' : 'Disconnected'}
                </div>
                {networkStatus?.ethernet.ip && (
                  <div className="text-xs text-slate-500 mt-1">
                    IP: {networkStatus.ethernet.ip} ({networkStatus.ethernet.dhcp ? 'DHCP' : 'Static'})
                  </div>
                )}
              </div>
            </div>

            <SectionHeader title="IP Configuration" />

            <div className="mt-4">
              <div className="flex gap-4 mb-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={localEthernetSettings.dhcp}
                    onChange={() => setLocalEthernetSettings(prev => ({ ...prev, dhcp: true }))}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-slate-200">DHCP (Automatic)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!localEthernetSettings.dhcp}
                    onChange={() => setLocalEthernetSettings(prev => ({ ...prev, dhcp: false }))}
                    className="w-4 h-4 text-blue-600"
                  />
                  <span className="text-sm text-slate-200">Static IP</span>
                </label>
              </div>

              <div className={`space-y-3 ${localEthernetSettings.dhcp ? 'opacity-50 pointer-events-none' : ''}`}>
                <TextInput
                  label="IP Address"
                  value={localEthernetSettings.ip || ''}
                  onChange={(v) => setLocalEthernetSettings(prev => ({ ...prev, ip: v }))}
                  placeholder="192.168.1.100"
                  disabled={localEthernetSettings.dhcp}
                />
                <div className="grid grid-cols-2 gap-3">
                  <TextInput
                    label="Subnet Mask"
                    value={localEthernetSettings.subnet || ''}
                    onChange={(v) => setLocalEthernetSettings(prev => ({ ...prev, subnet: v }))}
                    placeholder="255.255.255.0"
                    disabled={localEthernetSettings.dhcp}
                  />
                  <TextInput
                    label="Gateway"
                    value={localEthernetSettings.gateway || ''}
                    onChange={(v) => setLocalEthernetSettings(prev => ({ ...prev, gateway: v }))}
                    placeholder="192.168.1.1"
                    disabled={localEthernetSettings.dhcp}
                  />
                </div>
                <TextInput
                  label="DNS Server"
                  value={localEthernetSettings.dns?.join(', ') || ''}
                  onChange={(v) => setLocalEthernetSettings(prev => ({ ...prev, dns: v.split(',').map(s => s.trim()).filter(s => s) }))}
                  placeholder="8.8.8.8"
                  disabled={localEthernetSettings.dhcp}
                />
              </div>

              <button
                onClick={handleEthernetSave}
                className="mt-4 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium"
              >
                Apply Ethernet Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Access Point Panel */}
      {networkSubTab === 'ap' && (
        <div className="space-y-4">
          {/* AP Status */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-sm text-slate-400">Status</div>
                <div className={`text-lg font-medium ${networkStatus?.ap.active ? 'text-purple-400' : 'text-slate-500'}`}>
                  {networkStatus?.ap.active ? 'Active' : 'Inactive'}
                </div>
                {networkStatus?.ap.active && (
                  <div className="text-xs text-slate-500 mt-1">
                    {networkStatus.ap.clients} client(s) connected
                  </div>
                )}
              </div>
              {networkStatus?.ap.active ? (
                <button
                  onClick={stopAP}
                  disabled={stoppingAP}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-800 disabled:cursor-wait rounded-lg text-white font-medium flex items-center gap-2"
                >
                  {stoppingAP ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Stopping...
                    </>
                  ) : (
                    'Stop AP Mode'
                  )}
                </button>
              ) : (
                <button
                  onClick={handleStartAP}
                  disabled={startingAP}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-wait rounded-lg text-white font-medium flex items-center gap-2"
                >
                  {startingAP ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Starting...
                    </>
                  ) : (
                    'Start AP Mode'
                  )}
                </button>
              )}
            </div>

            <SectionHeader title="Access Point Settings" />

            <div className="space-y-3 mt-4">
              <div className="flex gap-2">
                <div className="flex-1">
                  <TextInput
                    label="SSID (Network Name)"
                    value={localApSSID}
                    onChange={setLocalApSSID}
                    placeholder="SolarController-XXXX"
                  />
                </div>
                <button
                  onClick={handleGenerateAPName}
                  className="self-end px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 text-sm"
                >
                  Generate
                </button>
              </div>

              <div className="relative">
                <TextInput
                  label="Password (min 8 characters)"
                  value={localApPassword}
                  onChange={setLocalApPassword}
                  placeholder="Enter password"
                />
                <button
                  onClick={() => setShowApPassword(!showApPassword)}
                  className="absolute right-2 bottom-2 text-slate-500 hover:text-slate-300"
                >
                  {showApPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            {networkStatus?.wifi.connected && (
              <div className="mt-4 p-3 bg-amber-900/20 border border-amber-700 rounded-lg">
                <div className="flex items-start gap-2 text-amber-200 text-sm">
                  <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Starting AP mode will disconnect from the current WiFi network.</span>
                </div>
              </div>
            )}
          </div>

          {/* Hostname */}
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <SectionHeader title="Device Identity" />
            <div className="flex gap-2 mt-4">
              <div className="flex-1">
                <TextInput
                  label="Hostname"
                  value={localHostname}
                  onChange={setLocalHostname}
                  placeholder="solar-controller"
                />
              </div>
              <button
                onClick={handleHostnameSave}
                className="self-end px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm"
              >
                Apply
              </button>
            </div>
          </div>

          {/* Factory Reset */}
          <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
            <SectionHeader title="Factory Reset" description="Clear all network settings and start in AP mode" />

            <button
              onClick={handleFactoryReset}
              className={`mt-4 w-full px-4 py-3 rounded-lg font-medium transition-colors ${
                factoryResetConfirm
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-red-900/50 hover:bg-red-800/50 border border-red-700 text-red-200'
              }`}
            >
              {factoryResetConfirm ? 'Click Again to Confirm Reset' : 'Factory Reset Network'}
            </button>
            {factoryResetConfirm && (
              <p className="text-xs text-red-300 mt-2 text-center">
                This will clear all saved WiFi networks and reset to AP mode
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderServicesTab = () => (
    <div className="space-y-6">
      {/* API Settings */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="REST API" description="HTTP API for external integrations" />

        <Toggle
          label="Enable API Access"
          description="Allow external applications to connect via HTTP"
          checked={externalServices.apiEnabled}
          onChange={(v) => setExternalServices(prev => ({ ...prev, apiEnabled: v }))}
        />

        {externalServices.apiEnabled && (
          <div className="space-y-3 mt-4 pt-4 border-t border-slate-700">
            <NumberInput
              label="API Port"
              value={externalServices.apiPort}
              onChange={(v) => setExternalServices(prev => ({ ...prev, apiPort: v }))}
              min={1}
              max={65535}
            />
            <Toggle
              label="Require Authentication"
              checked={externalServices.apiAuthEnabled}
              onChange={(v) => setExternalServices(prev => ({ ...prev, apiAuthEnabled: v }))}
            />
            {externalServices.apiAuthEnabled && (
              <div className="relative">
                <TextInput
                  label="API Key"
                  value={externalServices.apiKey}
                  onChange={(v) => setExternalServices(prev => ({ ...prev, apiKey: v }))}
                  placeholder="Enter API key"
                />
                <button
                  onClick={() => setExternalServices(prev => ({ ...prev, apiKey: crypto.randomUUID() }))}
                  className="absolute right-2 bottom-2 text-xs text-blue-400 hover:text-blue-300"
                >
                  Generate
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* MQTT Settings */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="MQTT" description="Publish data to an MQTT broker" />

        <Toggle
          label="Enable MQTT"
          description="Send real-time data to MQTT broker"
          checked={externalServices.mqttEnabled}
          onChange={(v) => setExternalServices(prev => ({ ...prev, mqttEnabled: v }))}
        />

        {externalServices.mqttEnabled && (
          <div className="space-y-3 mt-4 pt-4 border-t border-slate-700">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <TextInput
                  label="Broker Address"
                  value={externalServices.mqttBroker}
                  onChange={(v) => setExternalServices(prev => ({ ...prev, mqttBroker: v }))}
                  placeholder="mqtt.example.com"
                />
              </div>
              <NumberInput
                label="Port"
                value={externalServices.mqttPort}
                onChange={(v) => setExternalServices(prev => ({ ...prev, mqttPort: v }))}
                min={1}
                max={65535}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <TextInput
                label="Username"
                value={externalServices.mqttUsername}
                onChange={(v) => setExternalServices(prev => ({ ...prev, mqttUsername: v }))}
                placeholder="Optional"
              />
              <TextInput
                label="Password"
                value={externalServices.mqttPassword}
                onChange={(v) => setExternalServices(prev => ({ ...prev, mqttPassword: v }))}
                placeholder="Optional"
              />
            </div>
            <TextInput
              label="Base Topic"
              value={externalServices.mqttTopic}
              onChange={(v) => setExternalServices(prev => ({ ...prev, mqttTopic: v }))}
              placeholder="solar/controller"
            />
          </div>
        )}
      </div>

      {/* TCP Settings */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="TCP Socket" description="Raw TCP connection for custom integrations" />

        <Toggle
          label="Enable TCP Server"
          description="Accept TCP socket connections"
          checked={externalServices.tcpEnabled}
          onChange={(v) => setExternalServices(prev => ({ ...prev, tcpEnabled: v }))}
        />

        {externalServices.tcpEnabled && (
          <div className="space-y-3 mt-4 pt-4 border-t border-slate-700">
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <TextInput
                  label="Bind Address"
                  value={externalServices.tcpHost}
                  onChange={(v) => setExternalServices(prev => ({ ...prev, tcpHost: v }))}
                  placeholder="0.0.0.0"
                />
              </div>
              <NumberInput
                label="Port"
                value={externalServices.tcpPort}
                onChange={(v) => setExternalServices(prev => ({ ...prev, tcpPort: v }))}
                min={1}
                max={65535}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderExportTab = () => (
    <div className="space-y-6">
      {/* Export Mode */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="Export Limiting" description="Control how export power is managed" />

        <Toggle
          label="Enable Export Limiting"
          description="Actively limit power export to the grid"
          checked={formData.exportLimitEnabled}
          onChange={(v) => handleChange('exportLimitEnabled', v)}
        />
      </div>

      {/* Control Mode */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="Control Mode" description="Choose how limits are applied" />

        <div className="space-y-3">
          <Toggle
            label="Per-Phase Limiting"
            description="Apply limits to each phase individually instead of total"
            checked={formData.perPhaseLimit}
            onChange={(v) => handleChange('perPhaseLimit', v)}
          />

          <div className="pt-3 border-t border-slate-700">
            <label className="block text-sm text-slate-400 mb-2">Limit Type</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => handleChange('currentControl', false)}
                className={`px-4 py-3 rounded-lg border transition-colors ${
                  !formData.currentControl
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'bg-slate-800 border-slate-600 text-slate-300 hover:border-slate-500'
                }`}
              >
                <div className="font-medium">Power (W)</div>
                <div className="text-xs opacity-75">Limit by wattage</div>
              </button>
              <button
                onClick={() => handleChange('currentControl', true)}
                className={`px-4 py-3 rounded-lg border transition-colors ${
                  formData.currentControl
                    ? 'bg-blue-600 border-blue-500 text-white'
                    : 'bg-slate-800 border-slate-600 text-slate-300 hover:border-slate-500'
                }`}
              >
                <div className="font-medium">Current (A)</div>
                <div className="text-xs opacity-75">Limit by amperage</div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Limit Values */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader
          title={formData.currentControl ? "Current Limits" : "Power Limits"}
          description="Hard limit triggers immediate response, soft limit for gradual control"
        />

        <div className="space-y-4">
          {formData.currentControl ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <NumberInput
                    label="Hard Limit"
                    value={formData.exportCurrentHardLimit}
                    onChange={handleCurrentHardLimitChange}
                    unit="A"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Changing this auto-adjusts soft limit
                  </div>
                </div>
                <div>
                  <NumberInput
                    label="Soft Limit"
                    value={formData.exportCurrentSoftLimit}
                    onChange={(v) => handleSoftLimitChange('exportCurrentSoftLimit', 'exportCurrentHardLimit', v)}
                    unit="A"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Must be below hard limit
                  </div>
                </div>
              </div>
              <div className={`text-xs p-2 rounded ${
                formData.exportCurrentSoftLimit >= formData.exportCurrentHardLimit
                  ? 'bg-red-900/30 text-red-300'
                  : 'bg-slate-700/50 text-slate-400'
              }`}>
                Per-phase: {formData.perPhaseLimit ? 'Yes' : 'No'} |
                Margin: {formData.exportCurrentHardLimit - formData.exportCurrentSoftLimit}A
                {formData.exportCurrentSoftLimit >= formData.exportCurrentHardLimit && ' ⚠️ Soft must be below hard!'}
              </div>
            </>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <NumberInput
                    label="Hard Limit"
                    value={formData.exportPowerHardLimit}
                    onChange={handlePowerHardLimitChange}
                    unit="W"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Changing this auto-adjusts soft limit
                  </div>
                </div>
                <div>
                  <NumberInput
                    label="Soft Limit"
                    value={formData.exportPowerSoftLimit}
                    onChange={(v) => handleSoftLimitChange('exportPowerSoftLimit', 'exportPowerHardLimit', v)}
                    unit="W"
                  />
                  <div className="text-xs text-slate-500 mt-1">
                    Must be below hard limit
                  </div>
                </div>
              </div>
              <div className={`text-xs p-2 rounded ${
                formData.exportPowerSoftLimit >= formData.exportPowerHardLimit
                  ? 'bg-red-900/30 text-red-300'
                  : 'bg-slate-700/50 text-slate-400'
              }`}>
                Per-phase: {formData.perPhaseLimit ? 'Yes' : 'No'} |
                Margin: {formData.exportPowerHardLimit - formData.exportPowerSoftLimit}W
                ({((formData.exportPowerHardLimit - formData.exportPowerSoftLimit) / Math.abs(formData.exportPowerHardLimit) * 100).toFixed(1)}%)
                {formData.exportPowerSoftLimit >= formData.exportPowerHardLimit && ' ⚠️ Soft must be below hard!'}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Voltage Limits */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="Voltage Limits" description="Protect against over-voltage conditions" />

        <div className="grid grid-cols-2 gap-3">
          <div>
            <NumberInput
              label="Hard Limit"
              value={formData.voltageHardLimit}
              onChange={handleVoltageHardLimitChange}
              unit="V"
              min={200}
              max={280}
            />
            <div className="text-xs text-slate-500 mt-1">
              Changing this auto-adjusts soft limit
            </div>
          </div>
          <div>
            <NumberInput
              label="Soft Limit"
              value={formData.voltageSoftLimit}
              onChange={(v) => handleSoftLimitChange('voltageSoftLimit', 'voltageHardLimit', v)}
              unit="V"
              min={200}
              max={280}
            />
            <div className="text-xs text-slate-500 mt-1">
              Must be below hard limit
            </div>
          </div>
        </div>
        <div className={`text-xs p-2 rounded mt-3 ${
          formData.voltageSoftLimit >= formData.voltageHardLimit
            ? 'bg-red-900/30 text-red-300'
            : 'bg-slate-700/50 text-slate-400'
        }`}>
          Margin: {formData.voltageHardLimit - formData.voltageSoftLimit}V
          {formData.voltageSoftLimit >= formData.voltageHardLimit && ' ⚠️ Soft must be below hard!'}
        </div>
      </div>

      {/* Meter Target */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="Target Setpoint" description="The power value the controller aims for at the meter" />

        <NumberInput
          label="Meter Target"
          value={formData.meterTarget}
          onChange={(v) => handleChange('meterTarget', v)}
          unit="W"
        />
        <div className="text-xs text-slate-500 mt-2">
          Negative = slight import, Positive = slight export, Zero = balanced
        </div>
      </div>
    </div>
  );

  const renderAdvancedTab = () => (
    <div className="space-y-6">
      {/* Control Timing */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="Control Timing" description="How often control commands are sent to inverters" />

        <div className="space-y-4">
          <div>
            <NumberInput
              label="Control Command Interval"
              value={formData.controlCommandInterval || 2000}
              onChange={(v) => handleChange('controlCommandInterval', v)}
              unit="ms"
              min={1000}
              max={30000}
              step={500}
            />
            <div className="text-xs text-slate-500 mt-1">
              Minimum time between control commands (1000-30000ms). Higher = more stable but slower response.
            </div>
            <div className="mt-2 text-xs text-slate-400">
              Current: <span className="font-mono text-blue-400">{((formData.controlCommandInterval || 2000) / 1000).toFixed(1)}s</span> between commands
            </div>
          </div>

          <div>
            <NumberInput
              label="Post-Control Delay"
              value={formData.postControlDelay || 400}
              onChange={(v) => handleChange('postControlDelay', v)}
              unit="ms"
              min={100}
              max={2000}
              step={50}
            />
            <div className="text-xs text-slate-500 mt-1">
              Time to wait after sending control before resuming data collection (100-2000ms).
            </div>
            <div className="mt-2 text-xs text-slate-400">
              Current: <span className="font-mono text-blue-400">{formData.postControlDelay || 400}ms</span> pause after control
            </div>
          </div>

          {/* Periodic Heartbeat Section */}
          <div className="pt-4 border-t border-slate-700">
            <div className="flex items-center justify-between mb-3">
              <div>
                <label className="text-sm font-medium text-slate-300">Periodic Heartbeat</label>
                <div className="text-xs text-slate-500">
                  Send commands even when limit unchanged (inverter failsafe)
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleChange('periodicControlEnabled', !formData.periodicControlEnabled)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                  formData.periodicControlEnabled ? 'bg-blue-600' : 'bg-slate-600'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formData.periodicControlEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {formData.periodicControlEnabled && (
              <div>
                <NumberInput
                  label="Heartbeat Interval"
                  value={formData.periodicControlInterval || 30000}
                  onChange={(v) => handleChange('periodicControlInterval', v)}
                  unit="ms"
                  min={5000}
                  max={300000}
                  step={1000}
                />
                <div className="text-xs text-slate-500 mt-1">
                  How often to re-send the current limit even if unchanged (5-300 seconds).
                </div>
                <div className="mt-2 text-xs text-slate-400">
                  Current: <span className="font-mono text-blue-400">{((formData.periodicControlInterval || 30000) / 1000).toFixed(0)}s</span> heartbeat interval
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PID Controller */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="PID Controller" description="Proportional-Integral control parameters" />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <NumberInput
              label="Kp (Proportional)"
              value={formData.deratingKp}
              onChange={(v) => handleChange('deratingKp', v)}
              step={0.01}
              min={0}
            />
            <div className="text-xs text-slate-500 mt-1">
              Higher = faster response, may overshoot
            </div>
          </div>
          <div>
            <NumberInput
              label="Ki (Integral)"
              value={formData.deratingKi}
              onChange={(v) => handleChange('deratingKi', v)}
              step={0.001}
              min={0}
            />
            <div className="text-xs text-slate-500 mt-1">
              Higher = eliminates offset, may oscillate
            </div>
          </div>
        </div>
      </div>

      {/* Rate Limits */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="Rate Limits" description="Maximum change rate per control cycle" />

        <div className="grid grid-cols-2 gap-4">
          <div>
            <NumberInput
              label="Derating Limit"
              value={formData.deratingLimit}
              onChange={(v) => handleChange('deratingLimit', v)}
              unit="W/s"
              min={0}
            />
            <div className="text-xs text-slate-500 mt-1">
              Max rate to reduce power output
            </div>
          </div>
          <div>
            <NumberInput
              label="Increasing Limit"
              value={formData.increasingLimit}
              onChange={(v) => handleChange('increasingLimit', v)}
              unit="W/s"
              min={0}
            />
            <div className="text-xs text-slate-500 mt-1">
              Max rate to increase power output
            </div>
          </div>
        </div>
      </div>

      {/* Tuning Guide */}
      <div className="bg-blue-900/20 border border-blue-700/50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-blue-300 mb-2">PID Tuning Guide</h4>
        <ul className="text-xs text-blue-200 space-y-1">
          <li>Start with Kp=0.5, Ki=0.1 for stable response</li>
          <li>Increase Kp for faster response to export changes</li>
          <li>Increase Ki to eliminate steady-state error</li>
          <li>If oscillating, reduce Ki first, then Kp</li>
          <li>Rate limits prevent aggressive inverter commands</li>
        </ul>
      </div>
    </div>
  );

  const renderCommunicationsTab = () => (
    <div className="space-y-6">
      {/* Dev Mode Info */}
      {DEV_MODE && (
        <div className="bg-orange-900/20 border border-orange-700/50 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <svg className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-xs text-orange-200">
              <strong>Dev Mode Active:</strong> Defaults configured for local emulator (127.0.0.1:8502).
              Set <code className="bg-orange-900/50 px-1 rounded">DEV_MODE = false</code> in SettingsModal.tsx for production.
            </div>
          </div>
        </div>
      )}

      {/* Scan Configuration */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="Modbus Scan Settings" description="Configure device discovery parameters" />

        {!scanSettingsLoaded ? (
          <div className="text-slate-400 text-sm py-4 text-center">Loading scan settings from controller...</div>
        ) : (
        <div className="space-y-4">
          <TextInput
            label="Start IP Address"
            value={scanSettings.startIp}
            onChange={(v) => setScanSettings(prev => ({ ...prev, startIp: v }))}
            placeholder={CONFIG.scan.startIp}
          />

          <div className="grid grid-cols-2 gap-3">
            <NumberInput
              label="Start Port"
              value={scanSettings.startPort}
              onChange={(v) => setScanSettings(prev => ({ ...prev, startPort: v }))}
              min={1}
              max={65535}
            />
            <NumberInput
              label="Number of Ports"
              value={scanSettings.numberOfPorts}
              onChange={(v) => setScanSettings(prev => ({ ...prev, numberOfPorts: v }))}
              min={1}
              max={100}
            />
          </div>

          <NumberInput
            label="Number of IPs to Scan"
            value={scanSettings.numberOfIps}
            onChange={(v) => setScanSettings(prev => ({ ...prev, numberOfIps: v }))}
            min={1}
            max={255}
          />

          <NumberInput
            label="Scan Timeout"
            value={scanSettings.scanTimeout}
            onChange={(v) => setScanSettings(prev => ({ ...prev, scanTimeout: v }))}
            unit="ms"
            min={100}
            max={10000}
          />
        </div>
        )}
      </div>

      {/* Auto Rescan */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="Automatic Rescan" description="Periodically scan for new devices" />

        <Toggle
          label="Enable Auto Rescan"
          description="Automatically scan for devices at regular intervals"
          checked={scanSettings.autoRescan}
          onChange={(v) => setScanSettings(prev => ({ ...prev, autoRescan: v }))}
        />

        {scanSettings.autoRescan && (
          <div className="mt-4 pt-4 border-t border-slate-700">
            <NumberInput
              label="Rescan Interval"
              value={scanSettings.rescanInterval}
              onChange={(v) => setScanSettings(prev => ({ ...prev, rescanInterval: v }))}
              unit="min"
              min={1}
              max={1440}
            />
          </div>
        )}
      </div>

      {/* Manual Rescan */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="Manual Scan" description="Trigger an immediate device scan" />

        <button
          onClick={handleRescan}
          disabled={isScanning}
          className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 rounded-lg text-white font-medium transition-colors flex items-center justify-center gap-2"
        >
          {isScanning ? (
            <>
              <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Scanning... {scanProgress}%
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Scan for Devices
            </>
          )}
        </button>

        {isScanning && (
          <div className="mt-3">
            <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-300"
                style={{ width: `${scanProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Scan Error */}
        {scanError && (
          <div className="mt-3 p-3 bg-red-900/30 border border-red-700 rounded-lg">
            <div className="flex items-center gap-2 text-red-300 text-sm">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {scanError}
            </div>
          </div>
        )}

        {/* Scan Results */}
        {scanStatus && !isScanning && (
          <div className="mt-3 p-3 bg-green-900/30 border border-green-700 rounded-lg">
            <div className="text-green-300 text-sm font-medium mb-2">Scan Complete</div>
            <div className="grid grid-cols-2 gap-2 text-xs text-green-200">
              <div>Ports Found: <span className="font-mono">{scanStatus.portCount}</span></div>
              <div>Total Devices: <span className="font-mono">{scanStatus.deviceCount}</span></div>
              <div>PV Inverters: <span className="font-mono">{scanStatus.inverterCount}</span></div>
              <div>Hybrid Inverters: <span className="font-mono">{scanStatus.hybridCount}</span></div>
              <div>Meters: <span className="font-mono">{scanStatus.meterCount}</span></div>
            </div>
          </div>
        )}
      </div>

      {/* Scan Info */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="Scan Range Preview" />
        <div className="text-sm text-slate-300 font-mono bg-slate-900 rounded p-3">
          <div>IP Range: {scanSettings.startIp} ({scanSettings.numberOfIps} {scanSettings.numberOfIps === 1 ? 'host' : 'hosts'})</div>
          <div>Port Range: {scanSettings.startPort} - {scanSettings.startPort + scanSettings.numberOfPorts - 1}</div>
          <div>Total Addresses: {scanSettings.numberOfIps * scanSettings.numberOfPorts}</div>
        </div>
      </div>

      {/* Device Configuration */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader
          title="Device Configuration"
          description="View and configure connected devices. Edit rated power and model for devices not auto-detected correctly."
        />

        {!deviceConfigsLoaded ? (
          <div className="text-slate-400 text-sm py-4 text-center">Loading device configuration...</div>
        ) : deviceConfigs.length === 0 ? (
          <div className="text-slate-400 text-sm py-4 text-center">
            No devices found. Run a scan to discover devices.
          </div>
        ) : (
          <div className="space-y-3">
            {/* Group devices by port */}
            {(() => {
              const portGroups: { [portId: string]: DeviceConfig[] } = {};
              deviceConfigs.forEach(d => {
                if (!portGroups[d.portId]) portGroups[d.portId] = [];
                portGroups[d.portId].push(d);
              });

              return Object.entries(portGroups).map(([portId, devices]) => {
                const isExpanded = expandedPort === portId;
                const portType = devices[0]?.type || 'unknown';
                const typeColors: Record<string, string> = {
                  meter: 'border-l-blue-500',
                  inverter: 'border-l-amber-500',
                  hybrid: 'border-l-purple-500',
                  unknown: 'border-l-slate-500',
                };

                return (
                  <div key={portId} className={`bg-slate-900 rounded-lg border-l-4 ${typeColors[portType]} overflow-hidden`}>
                    {/* Port Header */}
                    <button
                      onClick={() => setExpandedPort(isExpanded ? null : portId)}
                      className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-slate-800/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm text-white">{portId}</span>
                        <span className="text-xs px-2 py-0.5 bg-slate-700 text-slate-300 rounded capitalize">
                          {portType}
                        </span>
                        <span className="text-xs text-slate-500">
                          {devices.length} {devices.length === 1 ? 'device' : 'devices'}
                        </span>
                      </div>
                      <svg
                        className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Expanded Device List */}
                    {isExpanded && (
                      <div className="border-t border-slate-700 divide-y divide-slate-700">
                        {devices.map((device) => {
                          const deviceKey = `${device.portId}-${device.deviceIndex}`;
                          const isEditing = editingDevice === deviceKey;

                          return (
                            <div key={deviceKey} className="px-4 py-3">
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-500 font-mono">#{device.deviceIndex}</span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                                    device.sleeping ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                                  }`}>
                                    {device.sleeping ? 'OFFLINE' : 'ONLINE'}
                                  </span>
                                </div>
                                <button
                                  onClick={() => setEditingDevice(isEditing ? null : deviceKey)}
                                  className="text-xs text-blue-400 hover:text-blue-300"
                                >
                                  {isEditing ? 'Done' : 'Edit'}
                                </button>
                              </div>

                              {isEditing ? (
                                <div className="space-y-3 mt-3">
                                  <div>
                                    <label className="block text-xs text-slate-400 mb-1">Model Name</label>
                                    <input
                                      type="text"
                                      value={device.model}
                                      onChange={(e) => {
                                        setDeviceConfigs(prev => prev.map(d =>
                                          d.portId === device.portId && d.deviceIndex === device.deviceIndex
                                            ? { ...d, model: e.target.value }
                                            : d
                                        ));
                                      }}
                                      placeholder="e.g., SG10KTL-M"
                                      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:outline-none focus:border-blue-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs text-slate-400 mb-1">Rated Power (W)</label>
                                    <input
                                      type="number"
                                      value={device.ratedPower}
                                      onChange={(e) => {
                                        setDeviceConfigs(prev => prev.map(d =>
                                          d.portId === device.portId && d.deviceIndex === device.deviceIndex
                                            ? { ...d, ratedPower: parseInt(e.target.value) || 0 }
                                            : d
                                        ));
                                      }}
                                      placeholder="e.g., 10000"
                                      min={0}
                                      max={1000000}
                                      className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded text-sm text-white focus:outline-none focus:border-blue-500"
                                    />
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    Note: Changes will be applied after saving settings.
                                  </div>
                                </div>
                              ) : (
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-slate-500">Model:</span>{' '}
                                    <span className="text-white">{device.model || '-'}</span>
                                  </div>
                                  <div>
                                    <span className="text-slate-500">Rated Power:</span>{' '}
                                    <span className="text-white">
                                      {device.ratedPower ? `${(device.ratedPower / 1000).toFixed(1)} kW` : '-'}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {/* Add Device Button */}
                        <div className="px-4 py-3">
                          <button
                            onClick={() => {
                              const newIndex = (Math.max(0, ...devices.map(d => parseInt(d.deviceIndex))) + 1).toString();
                              setDeviceConfigs(prev => [...prev, {
                                portId,
                                deviceIndex: newIndex,
                                type: portType,
                                model: '',
                                ratedPower: 0,
                                sleeping: false,
                              }]);
                              setEditingDevice(`${portId}-${newIndex}`);
                            }}
                            className="w-full px-3 py-2 border border-dashed border-slate-600 rounded text-sm text-slate-400 hover:text-slate-300 hover:border-slate-500 transition-colors"
                          >
                            + Add Device Manually
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
            })()}

            {/* Add New Port */}
            <div className="pt-3 border-t border-slate-700">
              <button
                onClick={() => {
                  const newPortId = prompt('Enter port ID (e.g., 192.168.1.100:502 or /dev/ttyAMA1):');
                  if (newPortId && newPortId.trim()) {
                    const portType = prompt('Enter device type (inverter, hybrid, or meter):') || 'inverter';
                    setDeviceConfigs(prev => [...prev, {
                      portId: newPortId.trim(),
                      deviceIndex: '1',
                      type: portType.toLowerCase(),
                      model: '',
                      ratedPower: 0,
                      sleeping: false,
                    }]);
                    setExpandedPort(newPortId.trim());
                    setEditingDevice(`${newPortId.trim()}-1`);
                  }
                }}
                className="w-full px-4 py-3 border border-dashed border-slate-600 rounded-lg text-sm text-slate-400 hover:text-slate-300 hover:border-slate-500 transition-colors flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add New Port / RS485 Connection
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderDatabaseTab = () => {
    const intervalWarnings: Record<number, { text: string; color: string }> = {
      1: { text: '~2MB/day - Fast SD wear', color: 'text-red-400' },
      5: { text: '~400KB/day - Recommended', color: 'text-green-400' },
      15: { text: '~130KB/day - Minimal storage', color: 'text-blue-400' },
    };

    const handleClearLogs = async () => {
      if (!window.confirm('Are you sure you want to delete all log files? This cannot be undone.')) {
        return;
      }
      setIsClearing(true);
      try {
        await clearLogs();
        refetchStatus();
      } finally {
        setIsClearing(false);
      }
    };

    return (
      <div className="space-y-6">
        {/* Storage Location */}
        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
          <SectionHeader title="Storage Location" description="Select where to store historical data (USB/SD recommended)" />

          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-sm text-slate-400 mb-1">Storage Device</label>
                <select
                  value={localDbSettings?.storagePath || ''}
                  onChange={(e) => setLocalDbSettings(prev => prev ? { ...prev, storagePath: e.target.value } : null)}
                  className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">-- Select storage device --</option>
                  {storageDevices.map((device) => (
                    <option key={device.path} value={device.path}>
                      {device.label} ({device.path}) - {device.freeMB > 1000 ? `${(device.freeMB / 1024).toFixed(1)} GB` : `${device.freeMB} MB`} free
                      {device.isRemovable ? ' (Removable)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => refetchDevices()}
                className="self-end px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-300 transition-colors"
                title="Refresh device list"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
            </div>

            {storageDevices.length === 0 && (
              <div className="text-sm text-amber-400 flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                No removable storage devices found. Insert a USB drive or SD card.
              </div>
            )}
          </div>
        </div>

        {/* Logging Interval */}
        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
          <SectionHeader title="Logging Interval" description="How often to record power data" />

          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              {[1, 5, 15].map((interval) => {
                const warning = intervalWarnings[interval];
                return (
                  <button
                    key={interval}
                    onClick={() => setLocalDbSettings(prev => prev ? { ...prev, loggingInterval: interval as 1 | 5 | 15 } : null)}
                    className={`px-4 py-3 rounded-lg border transition-colors ${
                      localDbSettings?.loggingInterval === interval
                        ? 'bg-blue-600 border-blue-500 text-white'
                        : 'bg-slate-800 border-slate-600 text-slate-300 hover:border-slate-500'
                    }`}
                  >
                    <div className="font-medium">{interval} min</div>
                    <div className={`text-xs ${localDbSettings?.loggingInterval === interval ? 'text-blue-200' : warning.color}`}>
                      {warning.text}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Storage Limits */}
        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
          <SectionHeader title="Storage Limits" />

          <div className="grid grid-cols-2 gap-4">
            <NumberInput
              label="Max Storage"
              value={localDbSettings?.maxStorageMB || 500}
              onChange={(v) => setLocalDbSettings(prev => prev ? { ...prev, maxStorageMB: v } : null)}
              unit="MB"
              min={50}
              max={10000}
            />
            <NumberInput
              label="Retention Period"
              value={localDbSettings?.retentionDays || 30}
              onChange={(v) => setLocalDbSettings(prev => prev ? { ...prev, retentionDays: v } : null)}
              unit="days"
              min={1}
              max={365}
            />
          </div>
        </div>

        {/* Enable Logging */}
        <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
          <Toggle
            label="Enable Data Logging"
            description="Record power data for historical charts"
            checked={localDbSettings?.loggingEnabled || false}
            onChange={(v) => setLocalDbSettings(prev => prev ? { ...prev, loggingEnabled: v } : null)}
          />

          {localDbSettings?.loggingEnabled && !localDbSettings?.storagePath && (
            <div className="mt-3 text-sm text-amber-400 flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Please select a storage device first
            </div>
          )}
        </div>

        {/* Storage Status */}
        {storageStatus && localDbSettings?.storagePath && (
          <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
            <SectionHeader title="Storage Usage" />

            <div className="space-y-3">
              {/* Progress bar */}
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-400">Used Space</span>
                  <span className="text-slate-300">{storageStatus.usedMB.toFixed(1)} MB / {storageStatus.maxMB} MB</span>
                </div>
                <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      storageStatus.usedPercent > 90 ? 'bg-red-500' :
                      storageStatus.usedPercent > 70 ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(storageStatus.usedPercent, 100)}%` }}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-slate-500">Files:</span>{' '}
                  <span className="text-slate-300">{storageStatus.fileCount}</span>
                </div>
                <div>
                  <span className="text-slate-500">From:</span>{' '}
                  <span className="text-slate-300">{storageStatus.oldestFile || '-'}</span>
                </div>
                <div>
                  <span className="text-slate-500">To:</span>{' '}
                  <span className="text-slate-300">{storageStatus.newestFile || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Danger Zone */}
        <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
          <SectionHeader title="Data Management" />

          <button
            onClick={handleClearLogs}
            disabled={isClearing || !storageStatus?.fileCount}
            className="w-full px-4 py-3 bg-red-900/50 hover:bg-red-800/50 disabled:bg-slate-800 disabled:text-slate-500 border border-red-700 disabled:border-slate-600 rounded-lg text-red-200 font-medium transition-colors flex items-center justify-center gap-2"
          >
            {isClearing ? (
              <>
                <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Clearing...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Clear All Logs
              </>
            )}
          </button>
          <p className="text-xs text-red-300/70 mt-2 text-center">
            This will permanently delete all historical data files
          </p>
        </div>
      </div>
    );
  };

  const renderSystemTab = () => (
    <div className="space-y-6">
      {/* System Info */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="System Information" />

        <div className="space-y-2 text-sm">
          <div className="flex justify-between py-2 border-b border-slate-700">
            <span className="text-slate-400">Controller Version</span>
            <span className="text-slate-200 font-mono">v1.0.0</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-700">
            <span className="text-slate-400">Uptime</span>
            <span className="text-slate-200 font-mono">--</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-700">
            <span className="text-slate-400">CPU Usage</span>
            <span className="text-slate-200 font-mono">--</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-slate-400">Memory Usage</span>
            <span className="text-slate-200 font-mono">--</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="System Actions" />

        <div className="space-y-3">
          <button className="w-full px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-200 font-medium transition-colors flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            Export Configuration
          </button>
          <button className="w-full px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-200 font-medium transition-colors flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Import Configuration
          </button>
          <button className="w-full px-4 py-3 bg-slate-700 hover:bg-slate-600 rounded-lg text-slate-200 font-medium transition-colors flex items-center justify-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Restart Controller
          </button>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4">
        <SectionHeader title="Danger Zone" description="These actions cannot be undone" />

        <div className="space-y-3">
          <button className="w-full px-4 py-3 bg-red-900/50 hover:bg-red-800/50 border border-red-700 rounded-lg text-red-200 font-medium transition-colors">
            Reset to Factory Defaults
          </button>
        </div>
      </div>

      {/* Logs */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <SectionHeader title="System Logs" />

        <div className="bg-slate-900 rounded p-3 h-32 overflow-y-auto font-mono text-xs text-slate-400">
          <div>[INFO] Controller started</div>
          <div>[INFO] Modbus scan complete: 3 devices found</div>
          <div>[INFO] Export control active</div>
          <div className="text-slate-500">-- End of logs --</div>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'network':
        return renderNetworkTab();
      case 'services':
        return renderServicesTab();
      case 'export':
        return renderExportTab();
      case 'advanced':
        return renderAdvancedTab();
      case 'communications':
        return renderCommunicationsTab();
      case 'database':
        return renderDatabaseTab();
      case 'system':
        return renderSystemTab();
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>

      {/* Modal - full width on mobile, slide from right on desktop */}
      <div className="relative ml-0 sm:ml-auto w-full sm:max-w-4xl bg-slate-900 sm:border-l border-slate-700 h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 bg-slate-900 border-b border-slate-700 p-3 sm:p-4">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              <h2 className="text-lg sm:text-xl font-bold text-white truncate">Controller Settings</h2>
              {DEV_MODE && (
                <span className="px-1.5 sm:px-2 py-0.5 text-xs font-semibold bg-orange-500/20 text-orange-400 border border-orange-500/50 rounded flex-shrink-0">
                  DEV
                </span>
              )}
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-white p-1 flex-shrink-0">
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs - scrollable on mobile */}
          <div className="flex gap-1 overflow-x-auto pb-1 -mb-1 -mx-3 px-3 sm:mx-0 sm:px-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
                }`}
              >
                <span className="[&>svg]:w-4 [&>svg]:h-4 sm:[&>svg]:w-5 sm:[&>svg]:h-5">{tab.icon}</span>
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4">
          {renderTabContent()}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 bg-slate-900 border-t border-slate-700 p-3 sm:p-4">
          <div className="flex gap-2 sm:gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-800 transition-colors font-medium text-sm sm:text-base"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
